/**
 * actionTypeConfig — Sprint 9 / neue Backend-API
 *
 * Icon + Farb-Mapping + Kategorie pro kind.
 * Die 16 echten kinds des BE (GET /api/v2/activities).
 * human_label kommt vom BE; diese Config liefert icon/color/category.
 * Defensiv: unbekannte kinds bekommen Default-Icon + Default-Farbe.
 */

export type ActivityCategory = 'rewards' | 'poc' | 'mobile' | 'status' | 'data'

export type ActionTypeConfig = {
  icon: string
  color: string
  softColor: string
  /** BE liefert human_label; diese Config-Label dient als Fallback-Label. */
  label: string
  category: ActivityCategory
}

export const ACTION_TYPE_CONFIG: Record<string, ActionTypeConfig> = {
  // --- Rewards ---
  payment: {
    icon: 'Coins',
    color: 'var(--hnt)',
    softColor: 'var(--primary-soft)',
    label: 'Zahlung',
    category: 'rewards',
  },
  iot_rewards: {
    icon: 'Sparkles',
    color: 'var(--iot)',
    softColor: 'var(--success-soft)',
    label: 'IOT-Rewards',
    category: 'rewards',
  },
  mobile_rewards: {
    icon: 'Sparkles',
    color: 'var(--mobile)',
    softColor: 'var(--accent-soft)',
    label: 'MOBILE-Rewards',
    category: 'rewards',
  },
  mobile_genesis: {
    icon: 'Zap',
    color: 'var(--mobile)',
    softColor: 'var(--accent-soft)',
    label: 'Mobile Genesis',
    category: 'rewards',
  },

  // --- PoC ---
  beacon: {
    icon: 'Radio',
    color: 'var(--teal)',
    softColor: 'var(--teal-soft)',
    label: 'Beacon',
    category: 'poc',
  },
  witness: {
    icon: 'Eye',
    color: 'var(--primary)',
    softColor: 'var(--primary-soft)',
    label: 'Witness',
    category: 'poc',
  },
  challenger: {
    icon: 'Crosshair',
    color: 'var(--info)',
    softColor: 'var(--info-soft)',
    label: 'Challenger',
    category: 'poc',
  },
  request: {
    icon: 'Send',
    color: 'var(--warning)',
    softColor: 'var(--warning-soft)',
    label: 'PoC-Request',
    category: 'poc',
  },

  // --- Mobile ---
  heartbeat: {
    icon: 'Activity',
    color: 'var(--mobile)',
    softColor: 'var(--accent-soft)',
    label: 'Heartbeat',
    category: 'mobile',
  },
  speedtest: {
    icon: 'Gauge',
    color: 'var(--mobile)',
    softColor: 'var(--accent-soft)',
    label: 'Speedtest',
    category: 'mobile',
  },

  // --- Data ---
  data: {
    icon: 'Database',
    color: 'var(--info)',
    softColor: 'var(--info-soft)',
    label: 'Data-Transfer',
    category: 'data',
  },

  // --- Status ---
  online: {
    icon: 'Wifi',
    color: 'var(--success)',
    softColor: 'var(--success-soft)',
    label: 'Online',
    category: 'status',
  },
  added: {
    icon: 'PlusCircle',
    color: 'var(--teal)',
    softColor: 'var(--teal-soft)',
    label: 'Hinzugefügt',
    category: 'status',
  },
  transfer: {
    icon: 'ArrowRightLeft',
    color: 'var(--warning)',
    softColor: 'var(--warning-soft)',
    label: 'Transfer',
    category: 'status',
  },
  location: {
    icon: 'MapPin',
    color: 'var(--danger)',
    softColor: 'var(--danger-soft)',
    label: 'Standort',
    category: 'status',
  },
  ping: {
    icon: 'Signal',
    color: 'var(--text-muted)',
    softColor: 'var(--surface-2)',
    label: 'Ping',
    category: 'status',
  },
}

export const ACTION_TYPE_DEFAULT: ActionTypeConfig = {
  icon: 'Clock',
  color: 'var(--text-muted)',
  softColor: 'var(--surface-2)',
  label: 'Aktivität',
  category: 'status',
}

export function getActionTypeConfig(kind: string): ActionTypeConfig {
  return ACTION_TYPE_CONFIG[kind] ?? ACTION_TYPE_DEFAULT
}

export const ALL_ACTION_TYPES = Object.keys(ACTION_TYPE_CONFIG) as string[]

/** Mapping: Kategorie → kinds-Set (für FilterBar-Tabs → API-Parameter) */
export const CATEGORY_KINDS: Record<ActivityCategory, string[]> = {
  rewards: ['payment', 'iot_rewards', 'mobile_rewards', 'mobile_genesis', 'rewards_v1', 'rewards_v2'],
  poc: [
    'beacon',
    'witness',
    'challenger',
    'request',
    'poc_receipts_v1', // = Witness
    'poc_request_v1', // = Beacon
    'poc_challengees',
    'poc_witnesses',
    'poc_challengers',
    'poc_challenge',
  ],
  mobile: ['heartbeat', 'speedtest'],
  data: ['data', 'state_channel_close_v1', 'data_credits'],
  status: ['online', 'added', 'transfer', 'location', 'ping'],
}

/**
 * R5/FD: Kategorie-Tab → action_type-Parameter fuer die API.
 *
 * Das Backend akzeptiert action_type seit R5 auch als Komma-Liste
 * ("heartbeat,speedtest"), deshalb filtern die Kategorie-Tabs jetzt
 * serverseitig statt clientseitig auf der bereits geladenen Seite.
 * null = keine Einschraenkung (Tab "Alle").
 */
export function categoryToActionType(category: ActivityCategory | 'all'): string | null {
  if (category === 'all') return null
  return CATEGORY_KINDS[category].join(',')
}
